import { Routes, Route, Navigate } from 'react-router-dom';
// Issue #465, epic #462. Identity is the platform's: the session provider, the
// route guards and the identity pages come from `@marinoscar/platform-web`.
import { RequireAuth, RequirePermission } from '@marinoscar/platform-web/identity/headless';
import { IdentityProviders } from './identity/IdentityProviders';
// Issues #466 and #464, epic #462. The platform host (api, viewer, applyTheme,
// feature switches) every packaged page and hook reads, and this app's
// settings-hub feature keys (a side-effect import: it registers them at module
// scope, before first render).
import { APP_NAME } from '@app/shared';
import { AppPlatformHostProvider } from './platform/platformHost';
// Issue #468, epic #462. The packaged Jobs, Job Insights and Worker Nodes pages
// take this app's table, spinner and API client from these two adapter objects.
import { JobsWebAdaptersProvider } from '@marinoscar/platform-web/jobs/headless';
import { NodesWebAdaptersProvider } from '@marinoscar/platform-web/nodes/headless';
import { appJobsAdapters } from './platform/jobsAdapters';
import { appNodesAdapters } from './platform/nodesAdapters';
// Issue #471, epic #467. The packaged Database Backup page takes this app's
// DataTable and transport through its adapters provider; the packaged Telemetry
// pages take their two assistant hooks and a spinner, and read the one
// `GET /api/telemetry/config` answer from `AppTelemetryConfigProvider`
// (`platform/TelemetryConfigBinding.tsx`), mounted ABOVE the platform host
// because the host reads the `telemetry` settings feature that answer drives.
import { DbBackupWebAdaptersProvider } from '@marinoscar/platform-web/db-backup/headless';
import {
  RequireTelemetryEnabled,
  TelemetryWebAdaptersProvider,
} from '@marinoscar/platform-web/telemetry/headless';
import { appDbBackupAdapters } from './platform/dbBackupAdapters';
import { appTelemetryAdapters } from './platform/telemetryAdapters';
import { AppTelemetryConfigProvider } from './platform/TelemetryConfigBinding';
// Issue #477, epic #472. The AI, AI models, AI usage and AI Provider pages are
// the platform's (`@marinoscar/platform-web/ai/ui`); they take this app's spinner
// through `AiWebAdaptersProvider`, and read the one `GET /api/ai/config` answer
// from `AppAiConfigProvider` (`platform/AiConfigBinding.tsx`), mounted ABOVE the
// platform host because the host reads the `ai` settings feature that answer
// drives. `RequireAiEnabled` is the feature half of the AI routes' gate.
import { AiWebAdaptersProvider } from '@marinoscar/platform-web/ai/headless';
import { RequireAiEnabled } from '@marinoscar/platform-web/ai/ui/require-ai-enabled';
import { appAiAdapters } from './platform/aiAdapters';
import { AppAiConfigProvider } from './platform/AiConfigBinding';
import './config/settingsFeatures';
// Issue #470, epic #467. The notification centre is the platform's; the slice is
// configured once at module scope in `platform/notifications.ts` (imported by
// `main.tsx`).
import { NotificationProvider } from '@marinoscar/platform-web/notifications/headless';
// Issue #484, epic #478. The shell is the platform's (`@marinoscar/platform-web/shell`):
// `ShellRoot` holds the viewer's light/dark/system choice (under the `theme_mode`
// key this app has always used) and mounts MUI's `ThemeProvider` and `CssBaseline`;
// `Layout` binds `ShellLayout`. `AppGraphFeatureProvider` is the one read of the
// connected-knowledge switch the `knowledge` destination and the hub cards gate on;
// it sits above the platform host, like the AI and telemetry config providers.
import { ShellRoot } from '@marinoscar/platform-web/shell/ui';
import { AppGraphFeatureProvider } from './platform/GraphFeatureBinding';
import { darkTheme, lightTheme } from './theme';
// Issue #22, epic #19 — the app-wide upload manager. Mounted around the
// authenticated shell (see the route element below), never per-page.
import { KvoxUploadManagerProvider } from '@marinoscar/kvox-web/transcripts/headless';
// Issue #499, epic #498. The domain web host binding, and the transcription slice's
// routes (data, spread where the literal route used to be).
import { AppKvoxHostProvider } from './platform-kvox/KvoxHostBinding';
import { renderKvoxRoutes } from '@marinoscar/kvox-web/shell';
import { appTranscriptsRoutes } from './kvox/transcriptsRoutes';
import { appNotesRoutes } from './kvox/notesRoutes';
import { appGraphRoutes } from './kvox/graphRoutes';
import { kvoxAskRoutes } from '@marinoscar/kvox-web/ask/ui';
import { kvoxSearchRoutes } from '@marinoscar/kvox-web/search/ui';
import { kvoxTranscriptionRoutes } from '@marinoscar/kvox-web/transcription/ui';
import { Layout } from './components/common/Layout';
import { ErrorBoundary } from './components/common/ErrorBoundary';
// Issue #258, epic #254; the platform's since #464. Eagerly imported, not lazy:
// the gate renders on the error path of a deployment that is deliberately out
// of service, and a code-split chunk fetched at that moment is one more thing
// that has to be working for the screen explaining why nothing is working to
// appear at all.
//
// PWA prompts (#219, epic #215), also packaged. Eagerly imported, not lazy:
// `UpdatePrompt` is what REGISTERS the service worker, and a registration
// deferred behind a dynamic import would not happen until React had already
// decided it was needed. Both render `null` in their default state. The
// service-worker hook is handed in: `virtual:pwa-register/react` exists only
// in this app's build (a Vitest alias stands in for it under test).
import { InstallPrompt, MaintenanceGate, UpdatePrompt } from '@marinoscar/platform-web/host/ui';
import { useRegisterSW } from 'virtual:pwa-register/react';

// Pages (lazy loaded)
import { Suspense, lazy } from 'react';
import { LoadingSpinner } from './components/common/LoadingSpinner';

// Issue #465, epic #462. The identity pages are the platform's
// (`@marinoscar/platform-web/identity/ui`). Login is this app's thin binding that
// puts the brand mark in the package's `Logo` slot; the callback, device
// activation and Access Tokens pages are used as packaged. Lazy like every
// other page: the package barrel is split into its own chunk.
const LoginPage = lazy(() => import('./identity/LoginPage'));
const AuthCallbackPage = lazy(() =>
  import('@marinoscar/platform-web/identity/ui').then((m) => ({ default: m.AuthCallbackPage })),
);
const ActivateDevicePage = lazy(() =>
  import('@marinoscar/platform-web/identity/ui').then((m) => ({ default: m.ActivateDevicePage })),
);
const HomePage = lazy(() => import('./pages/HomePage'));
// User settings — the hub (#96) plus one route per card in
// `config/userSettingsSections.tsx` (#91, epic #90). These replace the single
// stacked `UserSettingsPage`, which is deleted rather than left unrouted.
// The two content destinations (#106, over #30 epic #19 and #57 epic #45) —
// TWO pages at two routes since the Transcripts | Notes tab strip was deleted,
// where `LibraryPage` was one page at both. Plus the New-transcript flow, the
// viewer, and #31's history page. Lazy like every other page here, and with
// more reason than most: the viewer alone pulls in the virtualizer and the
// playback engine, neither of which a user who never opens a transcript needs.
const UserSettingsHubPage = lazy(() => import('./pages/UserSettingsHubPage'));
// Profile and Appearance are the platform's (#466, epic #462): the packaged
// pages over the packaged `useUserSettings`, themed through the host's
// `applyTheme` (`platform/platformHost.tsx`).
const UserProfilePage = lazy(() =>
  import('@marinoscar/platform-web/settings/ui').then((m) => ({ default: m.UserProfilePage })),
);
// `User`-prefixed to make explicit that it edits the signed-in user's own
// theme, not anything under the Console.
const UserAppearancePage = lazy(() =>
  import('@marinoscar/platform-web/settings/ui').then((m) => ({ default: m.UserAppearancePage })),
);
// Issue #126, epic #109; the platform's since #470 — the per-user event x
// channel notification matrix (`@marinoscar/platform-web/notifications/ui`).
const UserNotificationsPage = lazy(() =>
  import('@marinoscar/platform-web/notifications/ui').then((m) => ({
    default: m.UserNotificationsPage,
  })),
);
const UserTokensPage = lazy(() =>
  import('@marinoscar/platform-web/identity/ui').then((m) => ({ default: m.UserTokensPage })),
);
// Issue #80; the platform's since #480 (epic #478). The one `/settings/*`
// destination whose every action is permanent — its own `Danger Zone` group in
// `userSettingsSections.tsx`, and ungated here like every sibling below.
const UserDangerZonePage = lazy(() =>
  import('@marinoscar/platform-web/user-data/ui').then((m) => ({ default: m.UserDangerZonePage })),
);
// Issue #562. The admin counterpart: the factory reset, `system:factory_reset`.
const FactoryResetPage = lazy(() =>
  import('@marinoscar/platform-web/user-data/ui').then((m) => ({ default: m.FactoryResetPage })),
);
// Issue #532, Part B of #479 — "Download your data" (`@marinoscar/platform-web/exports/ui`), the
// page the `export.ready` notification links to. A registry card in `userSettingsSections.tsx`
// (its own `Your data` group above `Danger Zone`) and ungated here like every sibling.
const DataExportPage = lazy(() =>
  import('@marinoscar/platform-web/exports/ui').then((m) => ({ default: m.DataExportPage })),
);
// Issue #55, epic #45; the platform's since #477 (epic #472) — the user's OWN
// AI provider key (`UserAiKeysPage`). Every AI surface in that epic is
// unreachable until this page has been used once.
const UserAiKeysPage = lazy(() =>
  import('@marinoscar/platform-web/ai/ui').then((m) => ({ default: m.UserAiKeysPage })),
);
// Issue #279, epic #271; the platform's Get started page since #481 — the caller's own activation
// checklist, and the place the shell banner (#277) leads. A registry card in
// `userSettingsSections.tsx` like every sibling above, and ungated here for the same reason they
// are. This app's AI-key explanation rides in the page's `BeforeContent` slot.
const GettingStartedPage = lazy(() => import('./components/onboarding/GettingStartedRoute'));

// Console — the hub (#93) plus one route per card in
// `config/adminSections.tsx` (#92, epic #90).
const SettingsHubPage = lazy(() => import('./pages/Admin/SettingsHubPage'));
// Issue #124, epic #109; the platform's since #470 — the admin email
// configuration and its test send (`@marinoscar/platform-web/email/ui`).
const EmailSettingsPage = lazy(() =>
  import('@marinoscar/platform-web/email/ui').then((m) => ({ default: m.EmailSettingsPage })),
);
// Issue #225, epic #215; the platform's since #470 — the deployment-wide
// browser-notification policy.
const NotificationSettingsPage = lazy(() =>
  import('@marinoscar/platform-web/notifications/ui').then((m) => ({
    default: m.NotificationSettingsPage,
  })),
);
// Issue #355; the platform's since #470 — runtime-configurable Web Push (VAPID)
// key management, status and the test-push diagnostics.
const PushConfigPage = lazy(() =>
  import('@marinoscar/platform-web/notifications/ui').then((m) => ({
    default: m.PushConfigPage,
  })),
);
// Issue #55, epic #45; the platform's since #477 (epic #472) — the deployment's
// AI policy: this app's thin binding over the packaged `AiConfigPage`, with the
// task-model, graph and ceiling sections in its `Sections` slot. The AI models
// and AI usage pages are the packaged `AiModelsPage` and `AiUsagePage`.
const AiSettingsPage = lazy(() => import('./pages/Admin/AiSettingsPage'));
const AiModelsPage = lazy(() =>
  import('@marinoscar/platform-web/ai/ui').then((m) => ({ default: m.AiModelsPage })),
);
const AiUsagePage = lazy(() =>
  import('@marinoscar/platform-web/ai/ui').then((m) => ({ default: m.AiUsagePage })),
);
// Issue #258, epic #254 — the maintenance window's switch and its layers
// (the packaged page since #464). The screen a BLOCKED user sees is the
// packaged `MaintenanceGate`'s, not a page of this app.
const AdminMaintenancePage = lazy(() => import('./pages/Admin/MaintenancePage'));
// Issue #126, epic #118 — what is deployed here. Lazy like every other admin
// page: nobody who never opens the Console needs it in the entry chunk.
const AboutPage = lazy(() => import('./pages/Admin/AboutPage'));
// Issue #464, epic #462 — the platform's Doctor: the deployment's health checks
// and the support bundle. Lazy like every other admin page.
const DoctorPage = lazy(() => import('./pages/Admin/DoctorPage'));
// Issues #266, #271 (epic #254), packaged by #468 (epic #462) — the background
// queue's two Operations pages and the worker fleet, now the platform's
// (`@marinoscar/platform-web/jobs/ui` and `/nodes/ui`). Lazy like every other
// admin page: they pull in the shared DataTable and the node credential dialogs,
// and none of it is on the path of a user who never opens the Console. The
// packaged modules export named components, so each import maps to `default`.
const JobsPage = lazy(() =>
  import('@marinoscar/platform-web/jobs/ui').then((m) => ({ default: m.JobsPage })),
);
const JobInsightsPage = lazy(() =>
  import('@marinoscar/platform-web/jobs/ui').then((m) => ({ default: m.JobInsightsPage })),
);
const WorkersPage = lazy(() =>
  import('@marinoscar/platform-web/nodes/ui').then((m) => ({ default: m.WorkersPage })),
);
// Issue #469, epic #467 — the platform storage slice's configuration page
// (`@marinoscar/platform-web/storage/ui`). It takes no adapters: it reads the
// platform host the app already mounts. Lazy like every other admin page.
const StorageConfigPage = lazy(() =>
  import('@marinoscar/platform-web/storage/ui').then((m) => ({
    default: m.StorageConfigPage,
  })),
);
// Issue #287, epic #254; the platform's since #471 (epic #467) — the backup
// policy, the run history and the restore dialog
// (`@marinoscar/platform-web/db-backup/ui`). Lazy for the same reason: a
// DataTable, a policy form and the restore dialog that nobody who never opens
// the Console will ever mount.
const DbBackupPage = lazy(() => import('@marinoscar/platform-web/db-backup/ui'));
// Issue #471, epic #467 — the platform telemetry slice's three pages. Lazy, and
// through the package's per-page entries rather than its `telemetry/ui` barrel:
// `config/adminSections.tsx` imports the cards from the barrel, and a lazy
// import of the same module would be pulled into the main chunk with them.
const TelemetrySettingsPage = lazy(
  () => import('@marinoscar/platform-web/telemetry/ui/settings-page'),
);
const TelemetryExplorerPage = lazy(
  () => import('@marinoscar/platform-web/telemetry/ui/explorer-page'),
);
const TelemetryDashboardPage = lazy(
  () => import('@marinoscar/platform-web/telemetry/ui/dashboard-page'),
);
// Webhooks (#795): one packaged page, routed once per scope.
const WebhooksPage = lazy(() =>
  import('@marinoscar/platform-web/webhooks/ui').then((m) => ({ default: m.WebhooksPage })),
);
// Alerts (#795): the packaged page, and the firing banner the telemetry dashboard
// shows in its `BeforeContent` slot (telemetry does not depend on alerts; the app joins them here).
const AlertsPage = lazy(() =>
  import('@marinoscar/platform-web/alerts/ui').then((m) => ({ default: m.AlertsPage })),
);
const FiringAlertsBanner = lazy(() =>
  import('@marinoscar/platform-web/alerts/ui').then((m) => ({ default: m.FiringAlertsBanner })),
);
/** The telemetry dashboard's `BeforeContent` slot: the firing alerts, if any. */
function DashboardAlertsBanner() {
  return (
    <Suspense fallback={null}>
      <FiringAlertsBanner />
    </Suspense>
  );
}
const TELEMETRY_DASHBOARD_SLOTS = { BeforeContent: DashboardAlertsBanner };
// Issue #325, epic #319; the platform's since #470 — the admin broadcast list
// and its composer. Lazy for the same reason: a table, a composer dialog and a
// detail dialog that nobody who never opens the Console will ever mount.
const BroadcastsPage = lazy(() =>
  import('@marinoscar/platform-web/notifications/ui').then((m) => ({
    default: m.BroadcastsPage,
  })),
);
// Issue #278, epic #271; the platform's Setup guide since #481 — the deployment's first-run
// checklist. Lazy like every other admin page: an administrator who never opens the Console should
// not carry it in the entry chunk, and neither should a Viewer, who cannot reach the route at all.
const SetupPage = lazy(() => import('./components/onboarding/SetupGuideRoute'));
// The packaged Users & Allowlist page plus this app's Reminders tab (`slots.Tabs`).
const AdminUsersPage = lazy(() => import('./identity/UsersPage'));

// Test login page (development only)
const TestLoginPage = import.meta.env.PROD
  ? null
  : lazy(() => import('./pages/TestLoginPage'));

/** The two themes, as the shell's root takes them: module constants. */
const APP_THEMES = { light: lightTheme, dark: darkTheme };

/** Where the viewer's theme choice is kept in `localStorage`; unchanged by #484. */
const THEME_STORAGE_KEY = 'theme_mode';

function AppRoutes() {
  return (
    <>
      <ErrorBoundary>
        {/* THE CLIENT GATE (#258, epic #254), around the whole route tree and
            inside `AuthProvider`.

            Around everything, because a maintenance window is a property of the
            deployment rather than of any one page — the user's next click is
            refused wherever they are — and because swapping the subtree is what
            makes the screen's retry work: the pages unmount, and clearing the
            block remounts them so their own effects re-issue the requests that
            failed.

            Inside `AuthProvider` because the screen asks who is looking:
            `system_settings:read` decides whether it offers a link to the page
            that closes the window. That answer comes from the session already
            in memory, never from the API, which is refusing.

            An ordinary 503 with no marker never reaches it — see
            `services/maintenance.ts` for why that distinction is the feature. */}
        <MaintenanceGate appName={APP_NAME}>
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />

              {/* Test login (development only) */}
              {!import.meta.env.PROD && TestLoginPage && (
                <Route path="/testing/login" element={<TestLoginPage />} />
              )}

              {/* Protected routes */}
              <Route element={<RequireAuth loading={<LoadingSpinner fullScreen />} />}>
                {/* Device activation page - without layout for full-screen experience */}
                <Route path="/activate" element={<ActivateDevicePage />} />

                {/* The notification centre (#127, epic #109) wraps the SHELL,
                    not the whole app, and that scoping is the point:

                      * It is INSIDE `RequireAuth`, so it only ever mounts for
                        an authenticated user. Every endpoint it calls is
                        `@Auth()`-guarded and every one resolves the recipient from
                        the JWT, so mounting it on `/login` would buy a burst of
                        401s and a stream that cannot connect.
                      * It is around `Layout` specifically, because `Layout`'s
                        `AppBar` is where the bell lives. `/activate` above sits
                        outside the shell on purpose (full-screen device flow) and
                        correspondingly gets no bell and opens no stream.

                    ONE MOUNT POINT, so there is exactly one SSE connection per
                    tab. A provider mounted per-page would open and close a stream
                    on every navigation, which the server sees as a connection
                    storm from a single user and the client experiences as a bell
                    that resets its state every time the route changes. */}
                <Route
                  element={
                    <NotificationProvider>
                      {/* The upload manager (#22, epic #19) is mounted HERE,
                          for the same reason the notification centre above is
                          and with the same consequence: ONE mount point for
                          the whole authenticated shell.

                          An upload of a two-hour recording outlives every
                          route change a user makes while it runs. Mounted on
                          a page, the provider would unmount with that page and
                          take the in-flight `XMLHttpRequest`s with it — the
                          transfer would die mid-part, with nothing on screen to
                          say so. Mounted here, the engines live in a ref that
                          survives every navigation inside `Layout`.

                          INSIDE `RequireAuth`, like `NotificationProvider`:
                          every endpoint it calls is `@Auth()`-guarded, and
                          `/activate` deliberately sits outside the shell (a
                          full-screen device flow with no upload affordance).

                          Inside `NotificationProvider` rather than outside is
                          not significant — neither reads the other — but the
                          order is fixed so the tree is stable to read. */}
                      <KvoxUploadManagerProvider>
                        <Layout />
                      </KvoxUploadManagerProvider>
                    </NotificationProvider>
                  }
                >
                  <Route path="/" element={<HomePage />} />

                  {/* Transcripts (#30, epic #19; the slice's since #500, epic #498).

                      The four routes (`/transcripts`, `/transcripts/new`,
                      `/transcripts/:id`, `/transcripts/:id/history`) are DATA
                      exported by `@marinoscar/kvox-web/transcripts/ui`: their
                      paths, their lazy pages and the permissions they are
                      gated on (`transcripts:read` for the three read surfaces,
                      `transcripts:write` for the one that creates, which sends
                      a reader without it back to the list). `appTranscriptsRoutes`
                      is that data with the two pages that take slots swapped for
                      this app's wrappers (`kvox/transcriptsRoutes.ts`). This app
                      supplies the guard (`renderKvoxRoutes`).

                      ORDER IS NOT SIGNIFICANT — React Router ranks by
                      specificity, so `/transcripts/new` beats
                      `/transcripts/:id` wherever each is written. */}
                  {renderKvoxRoutes(appTranscriptsRoutes)}
                  {/* Notes (#57, epic #45; the slice's since #502, epic #498) — a
                      DESTINATION OF ITS OWN since #106. See
                      `config/destinations.ts`: four NON-PINNED destinations is
                      the bottom bar's ceiling, and #106 freed the fourth slot by
                      pinning Console rather than by merging these two nouns
                      onto one row.

                      The routes (`/notes`, `/notes/new`, `/notes/:id`,
                      `/notes/:id/history`) and the Note Templates settings route
                      (`/settings/note-templates`) are DATA exported by
                      `@marinoscar/kvox-web/notes/ui`: their paths, their lazy
                      pages and the permissions they are gated on, the exact
                      strings `notes.controller.ts` enforces (`notes:read` on
                      every read route, `notes:write` on the one that creates,
                      which sends a reader without it back to the list). The
                      settings route carries no gate, like every `/settings/*`
                      sibling. `appNotesRoutes` is that data with the four pages
                      that take slots or AI access swapped for this app's
                      wrappers (`kvox/notesRoutes.ts`). This app supplies the
                      guard (`renderKvoxRoutes`).

                      ORDER IS NOT SIGNIFICANT — React Router ranks by
                      specificity, so `/notes/new` beats `/notes/:id`. The page
                      routes are rendered here, at the notes position, rather
                      than the settings one: the same layout parent, so the same
                      result. */}
                  {renderKvoxRoutes(appNotesRoutes)}
                  {/* Knowledge graph (#373, epic #347; spec §13), the routes
                      of `@marinoscar/kvox-web/graph/ui`: their paths, their lazy
                      pages and the permission each is gated on come from
                      `kvoxGraphRoutes` (`/graph`, the overview landing since
                      #782; the entity index `/graph/entities`;
                      `/graph/entities/:id`, `/graph/explore`, the
                      `/graph/overview` redirect,
                      `/graph/imports/:proposalId` and the Knowledge graph
                      settings page `/settings/knowledge-graph`), with the
                      entity page swapped for this app's wrapper that fills
                      the Ask slots (`kvox/graphRoutes.ts`). OWNED BY
                      `knowledge` in `config/destinations.ts` since #438. This
                      app supplies the guard (`renderKvoxRoutes`). Declaration
                      order does not matter — React Router ranks by
                      specificity — so the settings route sits here beside its
                      slice's other routes. */}
                  {renderKvoxRoutes(appGraphRoutes)}
                  {/* Ask (#380, epic #348; spec §21.5), the route of
                      `@marinoscar/kvox-web/ask/ui`: `kvoxAskRoutes` carries the
                      path (`/ask/:conversationId?`, ONE route with an optional
                      segment so `/ask` → `/ask/:id` after the first question
                      keeps the page mounted), the lazy page and its gate,
                      `graph:read`, the exact string every `/api/ask/*` route
                      (#376, #378, #379) enforces. OWNED BY `knowledge` like the
                      graph routes above (#438; `home` before that).
                      `graphEnabled` is checked inside the page, which shows an
                      info state when it is off. This app supplies the guard
                      (`renderKvoxRoutes`). */}
                  {renderKvoxRoutes(kvoxAskRoutes)}
                  {/* The per-user settings surface (#96, epic #90) — the same
                      hub component `/admin/settings` renders, over
                      `USER_SETTINGS_SECTIONS`, plus one route per card.

                      NONE OF THESE IS WRAPPED IN `RequirePermission`, and that is
                      the deliberate difference from the `/admin/settings/*` block
                      below rather than an oversight. `RequireAuth` above
                      establishes that someone is signed in, and that is the only
                      question these routes have: they edit the caller's OWN
                      settings, which the API grants to all three roles, and
                      `config/userSettingsSections.tsx` correspondingly declares no
                      `permission` on any card. A gate here would deny a Viewer
                      their own display name.

                      As above, declaration order does not matter — React Router
                      v6 ranks by specificity, so `/settings/profile` beats
                      `/settings` wherever each is written. */}
                  <Route path="/settings" element={<UserSettingsHubPage />} />
                  {/* Issue #279, epic #271. Ungated like every `/settings/*`
                      sibling: the platform's `GET /api/onboarding` needs only
                      `user_settings:read`, which every role holds, because the
                      resource is the caller's own activation state. A
                      `RequirePermission` here would be a gate the API does not
                      have — and it would fail in the worst direction, shutting
                      a VIEWER (this application's default role) out of the one
                      page that explains why AI features are going to ask them
                      for a key. */}
                  <Route path="/settings/getting-started" element={<GettingStartedPage />} />
                  <Route path="/settings/profile" element={<UserProfilePage />} />
                  <Route path="/settings/appearance" element={<UserAppearancePage />} />
                  {/* Ungated like its siblings (#126): these are the caller's own
                      preferences, and the registry endpoint the page renders is
                      itself `@Auth()` with no permission for the same reason. */}
                  <Route path="/settings/notifications" element={<UserNotificationsPage />} />
                  {/* Issue #55, epic #45; the platform's since #477. UNLIKE its
                      unwrapped siblings this route IS gated, because the API
                      is: the platform's per-user AI routes (`/api/ai/keys`)
                      enforce `ai:use`, and the card declares `ai:use` +
                      `feature: 'ai'`. `RequireAiEnabled` is the feature half,
                      so a deep link replaces to `/` while AI is off instead of
                      showing a page of refused requests. */}
                  <Route
                    path="/settings/ai"
                    element={
                      <RequirePermission
                        permission="ai:use"
                        fallback={<Navigate to="/" replace />}
                      >
                        <RequireAiEnabled>
                          <UserAiKeysPage />
                        </RequireAiEnabled>
                      </RequirePermission>
                    }
                  />
                  {/* Search Indexing (#191, epic #165; the slice's since #501, epic
                      #498). The route is DATA exported by
                      `@marinoscar/kvox-web/search/ui`: its path and its lazy page.
                      Ungated like every `/settings/*` sibling, because the API's
                      `search-index.controller.ts` gates both routes on `@Auth()`
                      and no permission; the slice's own header says why. */}
                  {renderKvoxRoutes(kvoxSearchRoutes)}
                  <Route path="/settings/tokens" element={<UserTokensPage />} />
                  {/* Issue #80. Ungated like every `/settings/*` sibling, and
                      with the same reason `/settings/ai` carries: the platform
                      user-data controller (#480) gates its routes on `@Auth()`
                      and NO permission, because the resource is the caller's
                      own data, scoped by `userId` in the query itself. A
                      `RequirePermission` here would be a gate the API does not have, and it would fail in the worst
                      direction — a user unable to delete data this deployment
                      holds about them because of a role somebody else assigned.
                      The real gate is the typed confirmation in the page's
                      dialog, which is a deliberate act rather than an
                      authorization. */}
                  <Route path="/settings/danger-zone" element={<UserDangerZonePage />} />
                  {/* Issue #532. Ungated like every `/settings/*` sibling: `/api/exports` is
                      `@Auth()` and each source carries its own permission, `user_settings:read`
                      for the two the page offers every role. */}
                  <Route path="/settings/data-export" element={<DataExportPage />} />
                  {/* #795. The `Webhooks` cards' routes, each gated on the exact
                      `:read` string its card declares and its controller enforces
                      (`WEBHOOK_PERMISSIONS`; written as literals because the registry tests read
                      this file): `webhooks:read` (`/api/webhooks`, the caller's own) and
                      `system_webhooks:read` (`/api/admin/webhooks`). Writes are gated inside the
                      page. There is no organization route: this deployment runs single-org and
                      spreads no `feature: 'orgs'` card (see `adminSections.tsx`). */}
                  <Route
                    path="/settings/webhooks"
                    element={
                      <RequirePermission
                        permission="webhooks:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <WebhooksPage scope="user" />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/admin/settings/webhooks"
                    element={
                      <RequirePermission
                        permission="system_webhooks:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <WebhooksPage scope="system" />
                      </RequirePermission>
                    }
                  />
                  {/* #795. The `Alerts` card's route: `alerts:read`, the string
                      every read route of `/api/admin/alerts` enforces (`ALERT_PERMISSIONS`; a
                      literal because the registry tests read this file). Writes are gated inside
                      the page. NOT behind `RequireTelemetryEnabled`: node rules work while
                      telemetry is off. */}
                  <Route
                    path="/admin/settings/alerts"
                    element={
                      <RequirePermission
                        permission="alerts:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <AlertsPage />
                      </RequirePermission>
                    }
                  />
                  {/* Route-level AUTHORIZATION, not just authentication.
                      `RequireAuth` above only establishes that someone is
                      logged in — before this, a Viewer typing `/admin/settings`
                      reached the page and only then watched every API call 403.
                      `RequirePermission` was already in the codebase but had zero
                      usages; wrapping these routes is what turns it into the
                      enforcement point.

                      The permission on each route is the SAME string its card
                      declares in `config/adminSections.tsx`, which is the same
                      string the API's controller enforces — so the hub card, the
                      rail row, the menu entry and the route can no longer
                      disagree about who may go where.

                      ORDER IS NOT SIGNIFICANT HERE. React Router v6 ranks routes
                      by specificity rather than by declaration order, so
                      `/admin/settings/users` beats `/admin/settings` regardless
                      of where each sits in this list. They are grouped by surface
                      for reading, not for matching. */}

                  {/* Both redirects are REAL ROUTES, not catch-all fallout.
                      Without them a bookmarked `/admin/users` matches only `*`
                      and lands silently on `/` — the user asked for a page that
                      still exists and got the home screen with no explanation.
                      `replace` keeps the dead URL out of the history stack, so
                      Back returns to wherever the user came from rather than
                      bouncing through the redirect again.

                      They sit INSIDE `RequireAuth` so an unauthenticated
                      bookmark goes to login and arrives here afterwards, rather
                      than being redirected first and losing the destination. */}
                  <Route path="/admin" element={<Navigate to="/admin/settings" replace />} />
                  <Route
                    path="/admin/users"
                    element={<Navigate to="/admin/settings/users" replace />}
                  />

                  {/* The Console hub (#93, epic #90) — the searchable, grouped
                      card grid that reads `ADMIN_SECTIONS`. It replaces the
                      three-tab placeholder that answered this route through #92,
                      whose tabs duplicated the four routes below. That
                      duplication is now gone: the hub NAVIGATES to those routes
                      instead of re-hosting them. */}
                  {/* ANY-OF, and the one route here that is not a single
                      permission. This gate MUST STAY IN SYNC WITH `console`'s
                      `anyPermission` in `config/destinations.ts` — the two lists
                      answer the same question ("may this user reach the admin
                      surface?") on two different surfaces, and #92 left them
                      disagreeing: the Console row appeared in the rail, bottom
                      bar, user menu and quick actions for a `users:read`-only
                      user, whose click then bounced straight back to `/`. That
                      split brain is exactly what `config/destinations.ts`'s
                      header says the destination model exists to prevent, so the
                      route follows the destination rather than the reverse.

                      `requireAll` defaults to `false`, so `permissions` is an OR
                      here — matching `anyPermission`'s semantics, not
                      `hasAllPermissions`'.

                      A `users:read`-only user consequently reaches this route
                      and — since #93 — sees a hub containing exactly the one card
                      that permission unlocks, instead of the placeholder page's
                      blanket access-denied state. The hub's own gate
                      (`visibleSettingsSections`) does that per CARD, which is why
                      this route only answers the coarser question "may this user
                      reach the admin surface at all?". The five child routes
                      below keep their single-permission gates: each is one
                      specific page with one specific permission. */}
                  <Route
                    path="/admin/settings"
                    element={
                      <RequirePermission
                        permissions={['system_settings:read', 'users:read']}
                        fallback={<Navigate to="/" replace />}
                      >
                        <SettingsHubPage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #278, epic #271. Same permission string the `Setup
                      guide` card declares in `config/adminSections.tsx`, which
                      is the permission the platform onboarding slice adds the
                      `admin` block of `GET /api/onboarding` for (its default
                      `adminPermission`; no `onboarding:read` is minted,
                      following epic #118 decision 8's precedent for the About
                      card) — the invariant `destinations.test.ts` asserts for
                      every card. The page is read-only apart from the inline
                      invite, which gates itself on `allowlist:write`
                      internally: the route gate is about REACHABILITY, and
                      "what does this deployment still need" is the first thing
                      a new administrator should be able to open. */}
                  <Route
                    path="/admin/settings/setup"
                    element={
                      <RequirePermission
                        permission="system_settings:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <SetupPage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #124, epic #109. Same permission string the `Email`
                      card declares in `config/adminSections.tsx`, which is the
                      same string the API's email-settings controller enforces on
                      its GET — the invariant `destinations.test.ts` asserts for
                      every card. `system_settings:read` and not `:write`: saving
                      and test-sending need write, and the page disables both
                      without it, but the configuration is worth READING for
                      anyone diagnosing why mail is not arriving. */}
                  <Route
                    path="/admin/settings/email"
                    element={
                      <RequirePermission
                        permission="system_settings:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <EmailSettingsPage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #469, epic #467. `storage_config:read`, the same string
                      the `Storage` card declares and the platform's
                      storage-config controller enforces on its GET. Saving, the
                      connection test and bucket creation need
                      `storage_config:write`, which the page gates internally. */}
                  <Route
                    path="/admin/settings/storage"
                    element={
                      <RequirePermission
                        permission="storage_config:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <StorageConfigPage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #225, epic #215. `system_settings:read`, the same
                      string the `Notifications` card declares and the same one
                      `system-settings.controller.ts` enforces on its GET — the
                      invariant `destinations.test.ts` asserts for every card.
                      Saving needs `system_settings:write`, which the page gates
                      internally by disabling its controls. */}
                  <Route
                    path="/admin/settings/notifications"
                    element={
                      <RequirePermission
                        permission="system_settings:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <NotificationSettingsPage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #355. Same permission string the `Web Push` card
                      declares in `config/adminSections.tsx`, which is the
                      same string the API's push-config controller enforces on
                      its GET — the invariant `destinations.test.ts` asserts
                      for every card. `push:read` and not `:write`: generating,
                      rotating, enabling/disabling and removing all need
                      `push:write`, which the page disables without it, but
                      the configuration is worth READING for anyone diagnosing
                      why push notifications are not arriving. */}
                  <Route
                    path="/admin/settings/push"
                    element={
                      <RequirePermission
                        permission="push:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <PushConfigPage />
                      </RequirePermission>
                    }
                  />
                  {/* Transcription settings (#23, epic #19; the slice's since
                      #499, epic #498). The route is DATA exported by
                      `@marinoscar/kvox-web/transcription/ui`: its path, its lazy
                      page and the `system_settings:read` string the
                      `Transcription` card declares, which is the string the API's
                      transcription-settings controller enforces on its GET — the
                      invariant `destinations.test.ts` asserts for every card.
                      The slice's own header says why `:read` and not `:write`.
                      This app supplies the guard (`renderKvoxRoutes`). */}
                  {renderKvoxRoutes(kvoxTranscriptionRoutes)}
                  {/* Issue #55, epic #45; the platform's since #477 (epic #472).
                      Same permission string the `AI` card declares in
                      `config/adminSections.tsx`, which is the string the
                      platform's `ai-admin.controller` enforces on
                      `GET /api/admin/ai/config` (and `GET /api/ai-tasks/settings`
                      for this app's sections): `ai_config:read`. Saving needs
                      `ai_config:write`, which the PAGE gates by disabling its
                      controls.

                      ⚠ NOT BEHIND `RequireAiEnabled`, deliberately (AI rule 5):
                      this is where AI is switched on, so it must stay reachable
                      while AI is off. Its two siblings below are behind it,
                      exactly as their cards carry `feature: 'ai'`.

                      THREE ROUTES NESTED UNDER THE SETTINGS PATH, not tabs:
                      `settingsPageTitle`'s longest-prefix rule titles
                      `/admin/settings/ai/models` "AI models", not "AI". */}
                  <Route
                    path="/admin/settings/ai"
                    element={
                      <RequirePermission
                        permission="ai_config:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <AiSettingsPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/admin/settings/ai/models"
                    element={
                      <RequirePermission
                        permission="ai_config:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <RequireAiEnabled>
                          <AiModelsPage />
                        </RequireAiEnabled>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/admin/settings/ai/usage"
                    element={
                      <RequirePermission
                        permission="ai_config:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <RequireAiEnabled>
                          <AiUsagePage />
                        </RequireAiEnabled>
                      </RequirePermission>
                    }
                  />
                  {/* Issue #258, epic #254. Same permission string the
                      `Maintenance` card declares and the same one
                      the platform's maintenance controller
                      (`@marinoscar/platform-api/host`) enforces on its GET — the invariant `destinations.test.ts` asserts for
                      every card. Opening and closing a window needs
                      `system_settings:write`, which the page gates internally by
                      disabling its controls.

                      THIS IS ALSO THE ONE ROUTE `MaintenanceGate` NEVER COVERS,
                      mirroring `@AllowDuringMaintenance()` on the controller
                      behind it: the switch that ends a window has to be reachable
                      while the window is open, on both sides. */}
                  <Route
                    path="/admin/settings/maintenance"
                    element={
                      <RequirePermission
                        permission="system_settings:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <AdminMaintenancePage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #126, epic #118 (decision 8). Same permission string
                      the `About` card declares and the same one
                      `about/about.controller.ts` enforces on its GET — the
                      invariant `destinations.test.ts` asserts for every card.
                      Deliberately no `about:read`: "what is deployed here" is
                      an administrator's configuration read, and a permission
                      no role is seeded with would be a route nobody can open.
                      The page is read-only, so there is no write gate for it
                      to hold internally. */}
                  <Route
                    path="/admin/settings/about"
                    element={
                      <RequirePermission
                        permission="system_settings:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <AboutPage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #464, epic #462. `system_settings:read`, the same
                      string the `Doctor` card declares (the packaged
                      `doctorSettingsPage.card`) and the same one the platform's
                      `GET /api/admin/doctor` enforces. The page is read-only; the
                      support bundle download is the one action it offers. */}
                  <Route
                    path="/admin/settings/doctor"
                    element={
                      <RequirePermission
                        permission="system_settings:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <DoctorPage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #266, epic #254. `jobs:read` on both, the same
                      string the `Jobs` and `Job Insights` cards declare and the
                      same one `jobs/job-admin.controller.ts` enforces on its
                      list, stats and insights reads — the invariant
                      `destinations.test.ts` asserts for every card. Retrying,
                      deleting, sweeping and clearing the rollup all need
                      `jobs:write`, which each PAGE gates internally by omitting
                      the row actions and the sweep buttons.

                      TWO ROUTES, NOT A TAB. `/admin/settings/jobs/insights`
                      nests under the Jobs path deliberately, and React Router
                      v6 ranks by specificity, so the nested route wins wherever
                      it is declared. `settingsPageTitle`'s longest-prefix rule
                      is what keeps the compact AppBar titling it "Job Insights"
                      rather than "Jobs". */}
                  <Route
                    path="/admin/settings/jobs"
                    element={
                      <RequirePermission
                        permission="jobs:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <JobsPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/admin/settings/jobs/insights"
                    element={
                      <RequirePermission
                        permission="jobs:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <JobInsightsPage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #271, epic #254. Guarded EXACTLY as the two Jobs
                      routes above are, and on `nodes:read` — the literal string
                      `nodes/nodes-admin.controller.ts` enforces on its fleet
                      list, its node detail and its credential list, and the
                      same one the `Worker Nodes` card declares (the invariant
                      `settingsRegistry.test.ts` asserts against the API's own
                      constants file). Deleting a node and creating or revoking
                      a credential need `nodes:write`, which the PAGE gates
                      internally by omitting the row actions and the create
                      button — the route gate is about REACHABILITY.

                      ONE ROUTE, NOT TWO, and no tab: node credentials are
                      CONTENT of this page rather than a destination of their
                      own, because revoking a leaked worker token is an
                      incident-response action and a second card would put two
                      clicks in front of it. The full argument and the
                      alternatives rejected are in the packaged
                      `NodeCredentials` (`@marinoscar/platform-web/nodes/ui`). */}
                  <Route
                    path="/admin/settings/workers"
                    element={
                      <RequirePermission
                        permission="nodes:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <WorkersPage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #287, epic #254. Guarded EXACTLY as the Jobs and
                      Workers routes above are, and on `db_backup:read` — the
                      literal string the platform's `db-backup.controller`
                      (`@marinoscar/platform-api/db-backup`) enforces on its config read, its run list and its run
                      detail (`PERMISSIONS.DB_BACKUP_READ`), and the same one
                      the `Database Backup` card declares (the invariant
                      `destinations.test.ts` asserts for every card).

                      THREE PERMISSIONS BEHIND THIS ONE ROUTE, and only the
                      first is a reachability gate. Scheduling, cancelling and
                      deleting need `db_backup:write`; restoring and rolling
                      back need `db_backup:restore`, which the API keeps
                      SEPARATE from `write` precisely so it can be withheld from
                      someone who may schedule backups but must not be able to
                      replace the database. The PAGE gates both internally by
                      disabling its controls — widening this route gate to
                      either would make the page unreachable for the read-only
                      admin it is most useful to during an incident. */}
                  <Route
                    path="/admin/settings/db-backup"
                    element={
                      <RequirePermission
                        permission="db_backup:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <DbBackupPage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #325, epic #319. Guarded EXACTLY as the Jobs and
                      Workers routes above are, and on `broadcasts:read` — the
                      literal string
                      `notifications/broadcasts/broadcasts.controller.ts`
                      enforces on its audience count, its list and its detail
                      read (`PERMISSIONS.BROADCASTS_READ`), and the same one the
                      `Broadcasts` card declares (the invariant
                      `destinations.test.ts` asserts for every card). Composing,
                      cancelling, deleting and test-sending need
                      `broadcasts:write`, which the PAGE gates internally by
                      disabling its controls with a tooltip — the route gate is
                      about REACHABILITY.

                      The `/admin/settings` hub gate is deliberately NOT widened
                      to include this permission. It mirrors `console`'s
                      `anyPermission` in `config/destinations.ts` byte for byte
                      (asserted in `destinations.test.ts`), and every holder of
                      `broadcasts:read` is an admin who also holds
                      `system_settings:read`, so nothing is unreachable.
                      Widening one side without the other is exactly the
                      disagreement that test exists to catch. */}
                  <Route
                    path="/admin/settings/broadcasts"
                    element={
                      <RequirePermission
                        permission="broadcasts:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <BroadcastsPage />
                      </RequirePermission>
                    }
                  />
                  {/* `users:read` alone, even though the page also hosts the
                      allowlist. The route gate is about REACHABILITY and the page
                      is worth reaching for its Users tab; the Allowlist tab gates
                      its own content on `allowlist:read` inside the page. */}
                  <Route
                    path="/admin/settings/users"
                    element={
                      <RequirePermission
                        permission="users:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <AdminUsersPage />
                      </RequirePermission>
                    }
                  />
                  {/* Issue #471, epic #467. The platform telemetry slice's three
                      pages, APPENDED after the existing admin routes. Each route
                      is guarded on the exact string the platform's telemetry
                      controllers enforce, the same one its card declares:
                      `telemetry:read` for the settings page (`GET
                      /api/admin/telemetry/config`; saving needs
                      `telemetry:write`, which the PAGE gates by disabling its
                      controls), `telemetry:query` for the Explorer and the
                      Dashboard.

                      The settings page is NOT behind `RequireTelemetryEnabled`:
                      it is where telemetry is switched on. The Explorer and the
                      Dashboard are, exactly as their cards carry `feature:
                      'telemetry'`: with no store deployed, or collection off,
                      there is nothing to query, so a deep link replaces to `/`
                      instead of showing a page of failed requests. The guard
                      reads the shell's one copy of `GET /api/telemetry/config`
                      (`AppTelemetryConfigProvider`) and shows the app spinner
                      while the first answer is in flight.

                      TWO ROUTES NESTED UNDER THE SETTINGS PATH, not tabs:
                      `settingsPageTitle`'s longest-prefix rule titles
                      `/admin/settings/telemetry/explorer` "Telemetry
                      Explorer", not "Telemetry". */}
                  <Route
                    path="/admin/settings/telemetry"
                    element={
                      <RequirePermission
                        permission="telemetry:read"
                        fallback={<Navigate to="/" replace />}
                      >
                        <TelemetrySettingsPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/admin/settings/telemetry/explorer"
                    element={
                      <RequirePermission
                        permission="telemetry:query"
                        fallback={<Navigate to="/" replace />}
                      >
                        <RequireTelemetryEnabled>
                          <TelemetryExplorerPage />
                        </RequireTelemetryEnabled>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/admin/settings/telemetry/dashboard"
                    element={
                      <RequirePermission
                        permission="telemetry:query"
                        fallback={<Navigate to="/" replace />}
                      >
                        <RequireTelemetryEnabled>
                          <TelemetryDashboardPage slots={TELEMETRY_DASHBOARD_SLOTS} />
                        </RequireTelemetryEnabled>
                      </RequirePermission>
                    }
                  />
                  {/* Issue #562. The factory reset: `system:factory_reset`,
                      the exact SYSTEM permission the platform user-data
                      factory reset controller enforces (Admin only; never
                      implied by `system_settings:write`). Declared by the
                      pinned-last `Danger Zone` card in `adminSections.tsx`.
                      The page re-reads the user itself through the host's
                      `viewer.refresh`, so no props are passed. */}
                  <Route
                    path="/admin/settings/factory-reset"
                    element={
                      <RequirePermission
                        permission="system:factory_reset"
                        fallback={<Navigate to="/" replace />}
                      >
                        <FactoryResetPage />
                      </RequirePermission>
                    }
                  />
                </Route>
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </MaintenanceGate>
      </ErrorBoundary>
      {/* The PWA prompts (#219, epic #215) sit here — inside `ThemeProvider`
          so they are themed, OUTSIDE both `ErrorBoundary` and `Routes`, and
          outside `Layout`.

          Outside `Routes` because they belong to the DOCUMENT, not to any
          page: `UpdatePrompt` owns the service-worker registration, which must
          happen on `/login` and `/activate` too (those sessions run on the same
          precached shell, and the worker is also what makes notifications
          possible on Android at all). Mounting them inside `Layout` would tie
          both to the authenticated shell and re-run registration on every
          route change into and out of it.

          Outside `ErrorBoundary` because a page that has crashed is precisely
          when "a new version is available" is most likely to be the fix — a
          prompt inside the boundary would be replaced by the fallback along
          with the page.

          NEITHER RENDERS ANYTHING in its default state (no waiting worker, no
          captured install event), so a normal page load is pixel-identical to
          one before this change. */}
      <UpdatePrompt useRegisterSW={useRegisterSW} />
      <InstallPrompt appName={APP_NAME} />
    </>
  );
}

export default function App() {
  return (
    <ShellRoot themes={APP_THEMES} storageKey={THEME_STORAGE_KEY}>
      <IdentityProviders>
        <AppTelemetryConfigProvider>
          <AppAiConfigProvider>
            <AppGraphFeatureProvider>
              <AppPlatformHostProvider>
                <JobsWebAdaptersProvider adapters={appJobsAdapters}>
                  <NodesWebAdaptersProvider adapters={appNodesAdapters}>
                    <DbBackupWebAdaptersProvider adapters={appDbBackupAdapters}>
                      <TelemetryWebAdaptersProvider adapters={appTelemetryAdapters}>
                        <AiWebAdaptersProvider adapters={appAiAdapters}>
                          {/* The domain web host (#499, epic #498): the one port the
                              `@marinoscar/kvox-web` slices read their transport, viewer
                              and links from. Inside the identity providers, the graph
                              feature provider and the platform host (it reads all
                              three), above the router outlet. */}
                          <AppKvoxHostProvider>
                            <AppRoutes />
                          </AppKvoxHostProvider>
                        </AiWebAdaptersProvider>
                      </TelemetryWebAdaptersProvider>
                    </DbBackupWebAdaptersProvider>
                  </NodesWebAdaptersProvider>
                </JobsWebAdaptersProvider>
              </AppPlatformHostProvider>
            </AppGraphFeatureProvider>
          </AppAiConfigProvider>
        </AppTelemetryConfigProvider>
      </IdentityProviders>
    </ShellRoot>
  );
}
